import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { AlertTriangleIcon } from 'lucide-react';
function StarlightBackground() {
  // Generate random stars
  const stars = Array.from({
    length: 50
  }).map((_, i) => ({
    id: i,
    top: `${Math.random() * 100}%`,
    left: `${Math.random() * 100}%`,
    size: Math.random() * 2 + 1,
    delay: Math.random() * 5,
    duration: Math.random() * 3 + 2
  }));
  return <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-210">
      {stars.map(star => <motion.div key={star.id} className="absolute rounded-full bg-white" style={{
      top: star.top,
      left: star.left,
      width: star.size,
      height: star.size
    }} animate={{
      opacity: [0, 0.7, 0],
      scale: [0.5, 1, 0.5]
    }} transition={{
      duration: star.duration,
      repeat: Infinity,
      delay: star.delay,
      ease: 'easeInOut'
    }} data-id="element-211" />)}
    </div>;
}
export function ProblemSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const failures = ['Teams are deployed too early', 'Skill gaps surface mid-operation', 'Fatigue increases errors and risk', 'Missions slow down or fail entirely'];
  return <section ref={ref} className="w-full bg-slate-900 py-24 lg:py-32 relative overflow-hidden" data-id="element-212">
      {/* Starlight Effect */}
      <StarlightBackground data-id="element-213" />

      {/* Subtle amber glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-amber-500/5 rounded-full blur-[150px]" data-id="element-214" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10" data-id="element-215">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-216">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/20 bg-amber-500/5 mb-8" data-id="element-217">
            <AlertTriangleIcon className="w-3.5 h-3.5 text-amber-400" data-id="element-218" />
            <span className="text-xs font-mono text-amber-400 uppercase tracking-wider" data-id="element-219">
              Critical Gap
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-4" data-id="element-220">
            Hiring Data Doesn't Equal Readiness.
          </h2>

          <p className="text-xl text-slate-300 mb-16" data-id="element-221">
            Traditional systems track credentials. TalentIQ measures operational
            capability.
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto text-center mb-16" data-id="element-222">
          <p className="text-sm font-mono text-slate-500 uppercase tracking-wider mb-8" data-id="element-223">
            When readiness isn't measured:
          </p>

          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto" data-id="element-224">
            {failures.map((failure, i) => <motion.div key={failure} className="bg-slate-800/40 border border-amber-500/15 rounded-xl p-5 text-left" initial={{
            opacity: 0,
            y: 20
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.8 + i * 0.12
          }} data-id="element-225">
                <div className="flex items-start gap-3" data-id="element-226">
                  <div className="w-6 h-6 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-227">
                    <span className="text-xs font-mono text-amber-400 font-bold" data-id="element-228">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-slate-200" data-id="element-229">{failure}</span>
                </div>
              </motion.div>)}
          </div>
        </div>

        <motion.div initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.8,
        delay: 1.2
      }} className="text-center" data-id="element-230">
          <div className="w-16 h-px bg-slate-700 mx-auto mb-8" data-id="element-231" />
          <p className="text-lg text-slate-400" data-id="element-232">
            The cost isn't just financial.
          </p>
          <p className="text-xl text-white font-semibold mt-1" data-id="element-233">
            It's operational confidence.
          </p>
        </motion.div>
      </div>
    </section>;
}